"use client"

import useEmblaCarousel from "embla-carousel-react"
import { Check } from "lucide-react"
import {
  type MotionValue,
  motion,
  useMotionValue,
  useTransform,
} from "motion/react"
import { type CSSProperties, useEffect, useMemo } from "react"
import type { SanityImageAsset, SanityReference } from "sanity-codegen"
import { Cta, type SanityCtaProps } from "@/components/cta"
import { RichText, type RichTextValue } from "@/components/rich-text"
import { SlideOnView } from "@/components/slide-on-view"
import SanityImageField from "@/integrations/sanity/components/SanityImageField"
import { useIsMobile } from "@/ui/hooks/useIsMobile"
import { cn } from "@/utils/cn"

/** Flip to `true` to re-enable per-card CTAs */
const SHOW_CARD_CTA = false

interface CaseStudiesProps {
  content: {
    variant: "grid" | "cards"
    mobileLayout?: "carousel" | "grid"
    title: RichTextValue
    description: string
    cta: SanityCtaProps
    caseStudies: {
      imageOpacity?: number
      cta: SanityCtaProps
      image: { asset: SanityReference<SanityImageAsset> }
      caseStudy: {
        shortTitle: string
        shortDescription: string
      }
    }[]
  }
}

export function CaseStudies({ content }: CaseStudiesProps) {
  const isMobile = useIsMobile()
  const mobileLayout = content.mobileLayout ?? "carousel"

  return (
    <>
      {/**
       * Case study filters
      {content.variant === "grid" && (
        <div className="relative bottom-10 flex flex-wrap gap-4 px-6 md:px-16">
          <Badge label="TIPO DI PROGETTO" active={true} />
          <Badge label="TIPO DI PROGETTO" active={false} />
          <Badge label="TIPO DI PROGETTO" active={false} />
          <Badge label="TIPO DI PROGETTO" active={false} />
          <Badge label="TIPO DI PROGETTO" active={false} />
        </div>
      )}
        */}
      <div
        className={cn(
          "bg-neutral-light text-neutral-dark",
          content.variant === "cards" && "pt-5 md:pt-20"
        )}
      >
        {content.variant === "cards" ? (
          <>
            <div className="mx-auto max-w-[90rem] px-4">
              <SlideOnView delay={0.1} className="text-center md:text-left">
                <RichText value={content.title} />
              </SlideOnView>
              <SlideOnView
                delay={0.3}
                className="mt-6 text-center font-family-gthaptik text-lg font-light md:text-left xl:text-xlg"
              >
                {content.description}
              </SlideOnView>
              {/* <SlideOnView
                delay={0.4}
                className="mt-8 flex justify-center md:px-4"
              >
                <Cta sanity={content.cta} />
              </SlideOnView> */}
            </div>
            {isMobile ? (
              mobileLayout === "grid" ? (
                <CaseStudiesGrid caseStudies={content.caseStudies} />
              ) : (
                <MobileCarousel caseStudies={content.caseStudies} />
              )
            ) : (
              <div className="mt-8 hidden justify-center md:flex">
                {content.caseStudies.map((item, i) => (
                  <SlideOnView
                    className="flex-1"
                    delay={i * 0.2}
                    key={item.caseStudy.shortTitle}
                  >
                    <div
                      className="group relative h-[80rem] w-full max-w-[37rem] md:flex-shrink-0"
                      style={
                        {
                          "--image-max-opacity": item.imageOpacity ?? 1,
                        } as CSSProperties
                      }
                    >
                      <SanityImageField
                        asset={item.image?.asset}
                        responsive
                        objectFit="cover"
                        width="100%"
                        objectPosition="center"
                        className="pointer-events-none absolute inset-0 h-full w-full opacity-[var(--image-max-opacity)] md:scale-75 md:opacity-0 md:transition-all md:duration-300 md:group-hover:scale-100 md:group-hover:opacity-[var(--image-max-opacity)]"
                      />
                      <div className="absolute bottom-1/2 left-0 translate-y-1/2 p-6 transition-all group-hover:bottom-8 group-hover:translate-y-0">
                        <p className="text-8xl font-bold xl:text-9xl">
                          {String(i + 1).padStart(2, "0")}
                        </p>
                        <p className="mt-10 text-xl font-bold md:mt-16 md:text-2xl">
                          {item.caseStudy.shortTitle}
                        </p>
                        <p className="mt-3 text-base font-light md:mt-4 md:text-lg xl:text-xlg">
                          {item.caseStudy.shortDescription}
                        </p>
                        {SHOW_CARD_CTA && item.cta?.label && (
                          <div className="mt-8">
                            <Cta sanity={item.cta} />
                          </div>
                        )}
                      </div>
                    </div>
                  </SlideOnView>
                ))}
              </div>
            )}
          </>
        ) : (
          <CaseStudiesGrid caseStudies={content.caseStudies} />
        )}
      </div>
    </>
  )
}

function MobileCarousel({
  caseStudies,
}: {
  caseStudies: CaseStudiesProps["content"]["caseStudies"]
}) {
  const [emblaRef, api] = useEmblaCarousel({
    axis: "x",
  })

  const scrollValue = useMotionValue(0)

  useEffect(() => {
    if (!api) return

    const onScroll = () => {
      const rounded = Math.round(api.scrollProgress() * 1000) / 1000
      scrollValue.set(rounded)
    }

    api.on("scroll", onScroll)
    onScroll()

    return () => {
      api.off("scroll", onScroll)
    }
  }, [api, scrollValue])

  return (
    <div className="relative mt-8 block md:hidden">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {caseStudies.map((item, i) => (
            <div
              key={item.caseStudy.shortTitle}
              className="flex min-w-0 flex-[0_0_100%] justify-center"
            >
              <MobileCard
                cta={item.cta}
                title={item.caseStudy.shortTitle}
                description={item.caseStudy.shortDescription}
                image={item.image}
                index={i}
                startAnimationOffset={i / Math.max(1, caseStudies.length - 1)}
                endAnimationOffset={
                  (i + 1) / Math.max(1, caseStudies.length - 1)
                }
                motionValue={scrollValue}
                imageMaxOpacity={item.imageOpacity ?? 1}
              />
            </div>
          ))}
        </div>
      </div>
      {caseStudies.length > 1 && (
        <DotIndicators
          count={caseStudies.length}
          scrollProgress={scrollValue}
        />
      )}
    </div>
  )
}

function MobileCard({
  cta,
  index,
  image,
  title,
  description,
  motionValue,
  startAnimationOffset,
  endAnimationOffset,
  imageMaxOpacity,
}: {
  title: string
  description: string
  cta: CaseStudiesProps["content"]["caseStudies"][number]["cta"]
  index: number
  image: { asset: SanityReference<SanityImageAsset> }
  startAnimationOffset: number
  endAnimationOffset: number
  motionValue: MotionValue<number>
  imageMaxOpacity: number
}) {
  const value = useTransform(
    motionValue,
    [
      Math.min(0, startAnimationOffset),
      startAnimationOffset,
      Math.min(1, endAnimationOffset),
    ],
    [0, 1, startAnimationOffset >= 1 ? 1 : 0],
    {
      clamp: true,
    }
  )

  const top = useTransform(value, [0, 1], ["100%", "50%"])
  const y = useTransform(value, [0, 1], ["-100%", "-50%"])

  return (
    <motion.div
      style={{
        opacity: value,
      }}
      className="relative flex h-screen w-full items-center"
    >
      <motion.div
        className="absolute inset-0"
        style={{
          scale: value,
          opacity: imageMaxOpacity,
        }}
      >
        <SanityImageField
          asset={image?.asset}
          responsive
          objectFit="cover"
          width="100%"
          objectPosition="center"
        />
      </motion.div>
      <motion.div
        className="absolute z-10 p-6"
        style={{
          top,
          y,
        }}
      >
        <p className="text-7xl font-bold">
          {String(index + 1).padStart(2, "0")}
        </p>
        <p className="mt-10 text-xl font-bold">{title}</p>
        <p className="mt-3 text-base font-light">{description}</p>
        {SHOW_CARD_CTA && cta?.label && (
          <div className="mt-8">
            <Cta sanity={cta} />
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}

function DotIndicators({
  count,
  scrollProgress,
}: {
  count: number
  scrollProgress: MotionValue<number>
}) {
  const offsets = useMemo(
    () => Array.from({ length: count }, (_, i) => i / Math.max(1, count - 1)),
    [count]
  )

  return (
    <div className="absolute bottom-6 left-0 z-20 flex w-full items-center justify-center gap-2.5">
      {offsets.map((offset, i) => (
        <Dot
          key={offset}
          index={i}
          offset={offset}
          scrollProgress={scrollProgress}
        />
      ))}
    </div>
  )
}

function Dot({
  index,
  offset,
  scrollProgress,
}: {
  index: number
  offset: number
  scrollProgress: MotionValue<number>
}) {
  const width = useTransform(
    scrollProgress,
    [offset - 0.1, offset, offset + 0.1],
    [10, 28, 10],
    { clamp: true }
  )

  const isActive = useTransform(
    scrollProgress,
    (v) => Math.abs(v - offset) < 0.1
  )

  const backgroundColor = useTransform(isActive, (active) =>
    active ? "rgb(18, 0, 255)" : "rgb(204, 193, 248)"
  )

  return (
    <motion.div
      role="button"
      aria-label={`Go to slide ${index + 1}`}
      className="h-2.5 shrink-0 rounded-full"
      style={{ width, backgroundColor }}
    />
  )
}

function CaseStudiesGrid({
  caseStudies,
}: {
  caseStudies: CaseStudiesProps["content"]["caseStudies"]
}) {
  return (
    <div className="mt-8 grid grid-cols-1 gap-y-8 border-t border-viola-500 md:grid-cols-2 md:gap-y-0">
      {caseStudies.map((item, i) => (
        <SlideOnView delay={0.1} key={item.caseStudy.shortTitle}>
          <div
            className="group relative flex h-[65rem] max-h-screen w-full items-center justify-center transition-colors duration-300 md:h-[83rem]"
            style={
              {
                "--image-max-opacity": item.imageOpacity ?? 1,
              } as CSSProperties
            }
          >
            <SanityImageField
              asset={item.image?.asset}
              objectFit="cover"
              objectPosition="center"
              className="pointer-events-none absolute inset-0 h-full w-full opacity-[var(--image-max-opacity)] md:scale-75 md:opacity-0 md:transition-all md:duration-300 md:group-hover:scale-100 md:group-hover:opacity-[var(--image-max-opacity)]"
            />
            <div className="relative z-10 p-6">
              <p className="text-7xl font-bold md:text-8xl xl:text-9xl">
                0{i + 1}
              </p>
              <p className="mt-10 text-xl font-bold md:mt-16 md:text-2xl">
                {item.caseStudy.shortTitle}
              </p>
              <p className="mt-3 text-base font-light md:mt-4 md:text-lg xl:text-xlg">
                {item.caseStudy.shortDescription}
              </p>
              {SHOW_CARD_CTA && item.cta?.label && (
                <div className="mt-8">
                  <Cta
                    className="text-primary-500"
                    variant="ghost"
                    sanity={item.cta}
                  />
                </div>
              )}
            </div>
          </div>
        </SlideOnView>
      ))}
    </div>
  )
}

function _Badge({ label, active }: { label: string; active: boolean }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-3xl border px-3 py-2 font-family-gthaptik transition-colors duration-300",
        active
          ? "border-primary-600 bg-primary-600 text-neutral-light"
          : "border-neutral-dark bg-transparent text-neutral-dark"
      )}
    >
      {active && (
        <Check
          strokeWidth={4}
          size={20}
          className="rounded-full bg-neutral-light px-1 text-primary-600"
        />
      )}
      {label}
    </div>
  )
}
